<template>
    <FormSection
        title="Payer Information"
        :type="isHisSection ? 'his-section' : 'default-section'"
        :full-height="false"
        :application-id="referral.application"
    >
        <template #content>
            <div class="row px-4 py-2">
                <div class="col-12 col-md-5" v-if="isHisSection">
                    <div class="form-group col-12">
                        <label for="referral-race">
                            <span class="badge me-1" v-if="isHisSection">A1400</span> Payment Source (Check all that
                            apply)
                            <a
                                tabindex="0"
                                href="javascript:void(0);"
                                role="button"
                                v-custom-popover="{ content: sanitizeMarkup(payerSourceTooltip) }"
                            >
                                <i class="fas fa-info-circle text-primary font-size-xs"></i>
                            </a>
                        </label>
                        <div class="form-check p-0" v-for="[key, value] of paymentSources">
                            <div class="px-2 w-100">
                                <div class="form-check custom-checkbox form-check-inline">
                                    <input
                                        type="checkbox"
                                        class="form-check-input au-target"
                                        :id="`source${value.name}`"
                                        :value="value.value"
                                        :checked="referral.paymentSource?.includes(value.value)"
                                        :disabled="
                                            (referral.paymentSource?.length > 0 &&
                                                !isNoPaymentSourceChecked &&
                                                value.name === 'No payer source') ||
                                            (isNoPaymentSourceChecked && value.name !== 'No payer source')
                                        "
                                        @change="addPaymentSource(value.value)"
                                    />
                                    <label class="form-check-label" :for="`source${value.name}`">
                                        <span class="me-1">{{ value.name }}</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-12 col-md-5">
                    <div class="form-group col-12" v-if="!referral.medicareNumberNA && !!referral.medicareNumber">
                        <label id="Referral_Medicare_Number_Label" for="medicare-number">
                            <span class="badge me-1" v-if="isHisSection">A0600B</span> Medicare Number
                        </label>
                        <p>{{ referral.medicareNumber }}</p>
                    </div>
                    <div class="form-group col-12">
                        <label for="mbi-number" id="Mbi_Number_Label">
                            <span class="badge me-1" v-if="isHisSection">A0600B</span> Medicare Beneficiary Identifier
                            (MBI)
                            <span v-if="!referral.medicareBeneficiaryIdentificationNA" class="text-danger">*</span>
                            <a
                                tabindex="0"
                                href="javascript:void(0);"
                                role="button"
                                v-custom-popover="{ content: sanitizeMarkup(mbiTooltip) }"
                            >
                                <i class="fas fa-info-circle text-primary font-size-xs"></i>
                            </a>
                        </label>
                        <input
                            class="form-control"
                            maxlength="50"
                            name="mbi-number"
                            type="text"
                            placeholder="Enter Medicare Beneficiary Identifier (MBI)"
                            :value="formatToUppercase(referral.medicareBeneficiaryIdentification)"
                            :disabled="referral.medicareBeneficiaryIdentificationNA"
                            @change="(e) => handleMedicareNumber(e)"
                            @blur="v$.referral.medicareBeneficiaryIdentification.$touch"
                            :class="`${!v$.referral.medicareBeneficiaryIdentification.$error ? '' : 'field-invalid'}`"
                        />
                        <div class="form-group row">
                            <div class="col-12">
                                <div class="form-check custom-checkbox form-check-inline mt-2">
                                    <input
                                        type="checkbox"
                                        id="referral-medicare-beneficiary-identification-na"
                                        class="form-check-input au-target"
                                        v-model="referral.medicareBeneficiaryIdentificationNA"
                                    />
                                    <label
                                        class="form-check-label"
                                        for="referral-medicare-beneficiary-identification-na"
                                    >
                                        <span class="me-1">N/A- Not Applicable</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                        <span v-if="v$.referral.medicareBeneficiaryIdentification.$error" class="field-invalid"
                            >Medicare Beneficiary Identifier is required unless N/A is checked.</span
                        >
                    </div>
                    <div class="form-group col-12">
                        <i class="fas fa-question-circle"></i>
                        <a
                            href="https://www.cms.gov/Medicare/New-Medicare-Card/Providers/Providers-and-office-managers.html"
                            target="_blank"
                        >
                            Click here to review CMS guidelines on the New Medicare Beneficiary Identifier
                        </a>
                    </div>
                    <div class="form-group col-12">
                        <label for="medicaid-number">
                            <span class="badge me-1" v-if="isHisSection">A0700</span> Medicaid Number
                        </label>
                        <input
                            class="form-control"
                            maxlength="50"
                            placeholder="Enter Medicaid Number"
                            name="medicaid-number"
                            type="text"
                            v-model="referral.medicaidNumber"
                            :disabled="
                                otherMedicaid == otherMedicaidEnum?.MedicaidPending ||
                                otherMedicaid == otherMedicaidEnum?.NotAMedicaid
                            "
                        />
                        <div class="form-group row mb-0 ms-1">
                            <div class="form-check custom-radio form-check-inline mt-2">
                                <input
                                    type="radio"
                                    id="referral-medicaid-pending"
                                    name="other-medicaid"
                                    class="form-check-input au-target"
                                    :value="otherMedicaidEnum.MedicaidPending"
                                    :checked="otherMedicaid === otherMedicaidEnum.MedicaidPending"
                                    @click="otherMedicaidRadioClick(otherMedicaidEnum.MedicaidPending)"
                                />
                                <label class="form-check-label" for="referral-medicaid-pending">
                                    <span>Medicaid is pending</span>
                                </label>
                            </div>
                        </div>
                        <div class="form-group row ms-1">
                            <div class="form-check custom-radio form-check-inline mt-2">
                                <input
                                    type="radio"
                                    id="referral-no-medicaid"
                                    name="other-medicaid"
                                    class="form-check-input au-target"
                                    :value="otherMedicaidEnum?.NotAMedicaid"
                                    :checked="otherMedicaid === otherMedicaidEnum?.NotAMedicaid"
                                    @click="otherMedicaidRadioClick(otherMedicaidEnum?.NotAMedicaid)"
                                />
                                <label class="form-check-label" for="referral-no-medicaid">
                                    <span>Not a Medicaid Recipient</span>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-12 col-md-10">
                    <EligibilityCheck
                        :first-name="referral.firstName"
                        :last-name="referral.lastName"
                        :gender="referral.gender"
                        :location-id="referral.locationId"
                        :date-of-birth="referral.dateOfBirth"
                        :medicare-number="referral.medicareNumber"
                        :mbi-number="referral.medicareBeneficiaryIdentification"
                        :medicare-number-na="referral.medicareNumberNA"
                        :mbi-number-na="referral.medicareBeneficiaryIdentificationNA"
                        :application="referral.application"
                        :account-id="referral.accountId"
                        :missing-url="eligibilityMissingUrl"
                    />
                </div>
                <div class="col-12" v-if="referral.patientId">
                    <PayorTable
                        :add-new-payer="addNewPayer"
                        :get-current-patient-payors="getCurrentPatientPayors"
                        :get-patient-payors="getPatientPayors"
                        :update-patient-payor="updatePatientPayor"
                        :delete-patient-payor="deletePatientPayor"
                        :referral="referral"
                        :provider-id="providerId"
                    />
                </div>
            </div>
        </template>
    </FormSection>
</template>

<script lang="ts" src="./PayorInformation.ts"></script>
